import { useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { convertColorHexStringToNumber, notEmpty } from '@/utils';
import { CuboidClass } from '@/types';
import * as state from '@/components/AssetLabelling/state';
import { LabelClass, useGetStructureForAssetLabellingQuery } from '@/__generated__/graphql';

export const useGetStructure = (structureId: string) => {
  const setStructure = useSetAtom(state.structure);
  const setDefaultCuboidClassId = useSetAtom(state.defaultCuboidClassId);
  const setCuboidClassById = useSetAtom(state.cuboidClassById);
  const setLabelClassById = useSetAtom(state.labelClassById);

  const structureResult = useGetStructureForAssetLabellingQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'cache-first',
    skip: !structureId,
  });

  useEffect(() => {
    const { data, loading, error } = structureResult;
    if (loading) return;
    if (error) {
      throw new Error('Unable to fetch structure');
    }
    setStructure(data?.structure);

    const cuboidClasses = data?.structure?.cuboidClasses?.filter(notEmpty);

    setDefaultCuboidClassId(cuboidClasses?.at(0)?.id);

    if (cuboidClasses) {
      const cuboidClassById: { [id: string]: CuboidClass } = {};
      cuboidClasses.forEach(({ id, name, color }: LabelClass) => {
        cuboidClassById[id] = {
          id,
          name,
          color: convertColorHexStringToNumber(color),
          isVisible: true,
        };
      });
      setCuboidClassById(cuboidClassById);
    } else {
      setCuboidClassById(undefined);
    }

    const labelClasses = data?.structure?.labelClasses?.filter(notEmpty);
    if (labelClasses) {
      const labelClassById: { [id: string]: LabelClass & { isVisible: boolean } } = {};
      labelClasses.forEach((labelClass: LabelClass) => {
        labelClassById[labelClass.id] = {
          ...labelClass,
          isVisible: true,
        };
      });
      setLabelClassById(labelClassById);
    } else {
      setLabelClassById(undefined);
    }
  }, [
    structureResult,
    setStructure,
    setDefaultCuboidClassId,
    setCuboidClassById,
    setLabelClassById,
  ]);

  return { isLoading: structureResult.loading };
};
